import { useQuery, useQueryClient } from "@tanstack/react-query"
import { ArrowLeft } from "lucide-react"
import { useCallback, useState } from "react"
import { Link, useParams } from "react-router"

import { api } from "@/api/cliente"
import { ContratosDePersona } from "@/componentes/contratos/ContratosDePersona"
import { CarnetDePersona } from "@/componentes/personas/CarnetDePersona"
import { ChecklistLaboral } from "@/componentes/personas/ChecklistLaboral"
import { CuentaDePersona } from "@/componentes/personas/CuentaDePersona"
import { DocumentosDePersona } from "@/componentes/personas/DocumentosDePersona"
import { FichaPersona, type Persona } from "@/componentes/personas/FichaPersona"
import { FirmaDePersona } from "@/componentes/personas/FirmaDePersona"
import { PlanificacionDePersona } from "@/componentes/talento/planificacion/PlanificacionDePersona"
import { EstadoCarga, EstadoError } from "@/componentes/ui/Estados"
import { tienePermiso } from "@/lib/autorizacion"
import { usarSesion } from "@/lib/sesion"

/**
 * La ficha del empleado en una VISTA PROPIA (23/10, pedido del usuario): el expediente
 * laboral entero —ficha, checklist de documentos, contratos, firma, planificación,
 * carnet y, desde el 03/11, sus anticipos y préstamos— en `/talento-humano/personas/:id`,
 * en vez de un cajón lateral.
 *
 * ⚠ **El checklist alimenta la ficha**: «Subir y leer con IA» en cédula, RIF o currículo
 * abre el lector de la ficha con ese archivo; lo leído se revisa, se guarda con la ficha
 * y el archivo entra al expediente (la casilla queda cargada).
 *
 * ⚠ **Contratos, firma y planificación van FUERA del `<form>` de la ficha**, como en el
 * cajón: llevan su propio guardado.
 */
export function ExpedienteLaboral() {
  const { id } = useParams()
  const personaId = Number(id)
  const clienteQuery = useQueryClient()
  const usuario = usarSesion((e) => e.usuario)
  const [fichaSucia, setFichaSucia] = useState(false)
  const [aLeer, setALeer] = useState<File[] | null>(null)
  const atendido = useCallback(() => setALeer(null), [])

  const persona = useQuery({
    queryKey: ["persona", personaId],
    queryFn: () => api.get<Persona>(`/api/v1/personas/${personaId}`),
    enabled: Number.isFinite(personaId),
  })

  const volver = (
    <Link
      to="/talento-humano/personas"
      className="inline-flex items-center gap-1 text-sm underline-offset-2 hover:underline"
    >
      <ArrowLeft size={16} aria-hidden={true} /> Personas
    </Link>
  )
  if (persona.isPending) return <EstadoCarga etiqueta="Cargando la ficha…" />
  if (persona.isError) {
    return (
      <div className="space-y-4">
        {volver}
        <EstadoError titulo="No se pudo abrir la ficha" error={persona.error} />
      </div>
    )
  }
  const p = persona.data
  const empleado = p.tipo !== "CLIENTE"

  return (
    <div className="space-y-6">
      {volver}
      <div className="grid grid-cols-1 gap-6 xl:grid-cols-[minmax(0,1fr)_minmax(0,26rem)]">
        <section className="tarjeta p-4 sm:p-6">
          <FichaPersona
            key={p.id}
            persona={p}
            onSucio={setFichaSucia}
            leerArchivos={aLeer}
            onLeerAtendido={atendido}
            onGuardado={() => {
              clienteQuery.invalidateQueries({ queryKey: ["personas"] })
              clienteQuery.invalidateQueries({ queryKey: ["persona"] })
              // Lo leído con IA entra al expediente al guardar: el checklist cambia.
              clienteQuery.invalidateQueries({ queryKey: ["persona-checklist", p.id] })
              clienteQuery.invalidateQueries({ queryKey: ["persona-documentos", p.id] })
            }}
          />
        </section>
        <aside className="space-y-6">
          {empleado && (
            <div className="tarjeta p-4">
              <ChecklistLaboral personaId={p.id} onLeerConIa={(f) => setALeer([f])} />
            </div>
          )}
          <div className="tarjeta p-4">
            <DocumentosDePersona personaId={p.id} />
          </div>
        </aside>
      </div>

      {empleado && (
        <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
          <section className="tarjeta p-4">
            <ContratosDePersona personaId={p.id} cargoId={p.cargo_id ?? null} />
          </section>
          <section className="tarjeta p-4">
            <FirmaDePersona persona={p} haycambiosSinGuardar={fichaSucia} />
          </section>
          {tienePermiso(usuario, "th_calculo:ver") && (
            <section className="tarjeta p-4">
              <PlanificacionDePersona personaId={p.id} />
            </section>
          )}
          <section className="tarjeta p-4">
            <CarnetDePersona persona={p} />
          </section>
        </div>
      )}

      {/* Lo que debe por anticipos y préstamos (03/11): ver cuentas es otro permiso. */}
      {empleado && tienePermiso(usuario, "ingreso:ver") && (
        <section className="tarjeta p-4" aria-label="Anticipos y préstamos">
          <CuentaDePersona personaId={p.id} nombre={`${p.nombres} ${p.apellidos}`} />
        </section>
      )}
    </div>
  )
}
